import PropTypes from 'prop-types';
import React from 'react';
import Immutable from 'immutable';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { withRouter } from '@bibliocommons/utils-routing';
import { libraryShape, didNotFindOptionsShape, routerShape, searchQueryShape } from '@bibliocommons/bc-prop-types';
import { sendAnalyticsEvents } from '@bibliocommons/utils-analytics';
import { searchEventComposer } from '@bibliocommons/analytics-event-composers';

import { DocumentHeadContainer } from 'app/components/shared/DocumentHead';
import AvailabilityDetailActions from 'app/actions/AvailabilityDetailActions';
import CardsActions from 'app/actions/CardsActions';
import DrawerActions from 'app/actions/DrawerActions';

import ListPresentationActions from 'app/actions/ListPresentationActions';
import RecommendationActions from 'app/actions/RecommendationActions';
import GroupSearchFeedbackActions from 'app/actions/GroupSearchFeedbackActions';
import SearchActions from 'app/actions/SearchActions';
import { selectCurrentUser } from 'app/selectors/AuthSelector';
import { selectCurrentLibrary } from 'app/selectors/LibrarySelector';
import { selectSearchResultBib } from 'app/selectors/SearchSelector';
import { selectCardsForSearch } from 'app/selectors/CardsSelector';
import EmptySearchResultContainer from 'app/components/search/EmptySearchResult/EmptySearchResultContainer';
import GroupSearchFeedbackContainer from 'app/components/GroupSearchFeedback/GroupSearchFeedbackContainer';

import Search from './Search';

export class SearchContainer extends React.PureComponent {
  componentDidMount() {
    const { searchType, query, zeroResults, didYouMeanQuerySuggestion } = this.props;

    sendAnalyticsEvents(
      searchEventComposer.composeInitialPageviewSearchEvent({
        searchType,
        query,
        didYouMeanQuerySuggestion,
        zeroResults
      })
    );
  }

  componentDidUpdate(prevProps) {
    this.resetPageOnViewChange(prevProps);
  }

  resetPageOnViewChange(prevProps) {
    const { router } = this.props;

    if (this.props.currentView !== prevProps.currentView) {
      router.replace({
        query: {
          ...router.query,
          page: undefined
        }
      });
    }
  }

  renderContent() {
    const {
      activeFilters,
      branches,
      broadened,
      broadenedSearchCount,
      broadenedSearchType,
      catalogSearchOptions,
      currentView,
      customEdit,
      didYouMeanQuerySuggestion,
      didYouMeanResultCount,
      drawer,
      featuredContent,
      featuredContentBlockPosition,
      fields,
      isFetching,
      pagination,
      query,
      recommendation,
      resultPaneActive,
      results,
      rssURL,
      searchQuery,
      searchType,
      singleBranch,
      sortBys,
      suppress,
      titleKey,
      zeroResults,
      didNotFindOptions,
      shelfName,
      featuredCards
    } = this.props;

    // Actions
    const {
      availabilityDetailActions,
      drawerActions,
      groupSearchFeedbackActions,
      listPresentationActions,
      recommendationActions,
      searchActions,
      chatWidgets,
      cardsActions
    } = this.props;

    if (zeroResults) {
      return <EmptySearchResultContainer />;
    }

    return (
      <div>
        <GroupSearchFeedbackContainer searchResults={results} />
        <Search
          activeFilters={activeFilters}
          availabilityDetailActions={availabilityDetailActions}
          branches={branches}
          broadened={broadened}
          broadenedSearchCount={broadenedSearchCount}
          broadenedSearchType={broadenedSearchType}
          catalogSearchOptions={catalogSearchOptions}
          currentUser={this.props.currentUser}
          currentView={currentView}
          customEdit={customEdit}
          didYouMeanQuerySuggestion={didYouMeanQuerySuggestion}
          didYouMeanResultCount={didYouMeanResultCount}
          drawer={drawer}
          drawerActions={drawerActions}
          featuredContent={featuredContent}
          featuredContentBlockPosition={featuredContentBlockPosition}
          fields={fields}
          groupSearchFeedbackActions={groupSearchFeedbackActions}
          isFetching={isFetching}
          library={this.props.library}
          libraryShortName={this.props.libraryShortName}
          listPresentationActions={listPresentationActions}
          locked={this.props.locked}
          pagination={pagination}
          query={query}
          recommendation={recommendation}
          recommendationActions={recommendationActions}
          resultPaneActive={resultPaneActive}
          results={results}
          rssURL={rssURL}
          searchActions={searchActions}
          searchQuery={searchQuery}
          searchType={searchType}
          showExternalSearchLink={this.props.showExternalSearchLink}
          singleBranch={singleBranch}
          sortBys={sortBys}
          suppress={suppress}
          titleKey={titleKey}
          zeroResults={zeroResults}
          didNotFindOptions={didNotFindOptions}
          orderedSearchTypes={this.props.orderedSearchTypes}
          chatWidgets={chatWidgets}
          shelfName={shelfName}
          featuredCards={featuredCards}
          cardsActions={cardsActions}
        />
      </div>
    );
  }

  render() {
    const { intl } = this.props;

    return (
      <div className="container">
        <DocumentHeadContainer pageTitle={intl.formatMessage({ id: 'searchTitle' })} />
        {this.renderContent()}
      </div>
    );
  }
}

SearchContainer.propTypes = {
  activeFilters: ImmutablePropTypes.map,
  availabilityDetailActions: PropTypes.objectOf(PropTypes.func).isRequired,
  branches: ImmutablePropTypes.list,
  broadened: PropTypes.bool,
  broadenedSearchCount: PropTypes.number,
  broadenedSearchType: PropTypes.string,
  catalogSearchOptions: ImmutablePropTypes.map,
  currentUser: ImmutablePropTypes.map,
  currentView: PropTypes.string,
  customEdit: PropTypes.bool,
  didYouMeanQuerySuggestion: PropTypes.string,
  didYouMeanResultCount: PropTypes.number,
  drawer: ImmutablePropTypes.map,
  drawerActions: PropTypes.objectOf(PropTypes.func).isRequired,
  featuredContent: ImmutablePropTypes.map,
  featuredContentBlockPosition: PropTypes.number,
  fields: ImmutablePropTypes.list,
  groupSearchFeedbackActions: PropTypes.objectOf(PropTypes.func).isRequired,

  isFetching: PropTypes.bool,
  library: libraryShape,
  libraryShortName: PropTypes.string.isRequired,
  listPresentationActions: PropTypes.objectOf(PropTypes.func).isRequired,
  locked: PropTypes.bool,
  pagination: ImmutablePropTypes.map,
  query: PropTypes.string,
  recommendation: ImmutablePropTypes.map,
  recommendationActions: PropTypes.objectOf(PropTypes.func).isRequired,
  resultPaneActive: PropTypes.bool,
  results: ImmutablePropTypes.list,
  rssURL: PropTypes.string,
  featuredCards: ImmutablePropTypes.list.isRequired,
  searchActions: PropTypes.objectOf(PropTypes.func).isRequired,
  cardsActions: PropTypes.objectOf(PropTypes.func).isRequired,
  searchQuery: searchQueryShape,
  searchType: PropTypes.string.isRequired,
  shelfName: PropTypes.string,
  showExternalSearchLink: PropTypes.bool,
  singleBranch: PropTypes.bool,
  sortBys: ImmutablePropTypes.list,
  suppress: PropTypes.bool,
  titleKey: PropTypes.string,
  zeroResults: PropTypes.bool,
  didNotFindOptions: didNotFindOptionsShape,
  chatWidgets: ImmutablePropTypes.mapOf(PropTypes.object),
  orderedSearchTypes: ImmutablePropTypes.list,
  router: routerShape.isRequired
};

const mapStateToProps = state => {
  const catalogSearch = state.getIn(['search', 'catalogSearch']);
  const library = selectCurrentLibrary(state);
  return {
    activeFilters: catalogSearch.get('activeFilters'),
    branches: library.get('branches'),
    broadened: catalogSearch.get('broadened'),
    broadenedSearchCount: catalogSearch.get('broadenedSearchCount'),
    broadenedSearchType: catalogSearch.get('broadenedSearchType'),
    catalogSearchOptions: state.getIn(['search', 'catalogSearchOptions']),
    currentUser: selectCurrentUser(state),
    currentView: catalogSearch.get('view'),
    customEdit: catalogSearch.get('custom_edit'),
    didYouMeanQuerySuggestion: catalogSearch.get('didYouMeanQuerySuggestion'),
    didYouMeanResultCount: catalogSearch.get('didYouMeanResultCount'),
    drawer: state.get('drawer'),
    featuredContentBlockPosition: library.get('featuredContentBlockPosition'),
    fields: catalogSearch.get('fields'),
    isFetching: catalogSearch.get('isFetching'),
    libraryShortName: library.get('shortName'),
    orderedSearchTypes: library.get('orderedSearchTypes', Immutable.List()),
    locked: catalogSearch.get('locked'),
    pagination: catalogSearch.get('pagination'),
    query: catalogSearch.get('query', ''),
    recommendation: state.get('recommendation'),
    resultPaneActive: catalogSearch.get('resultPaneActive'),
    results: selectSearchResultBib(state),
    featuredCards: selectCardsForSearch(state),
    rssURL: catalogSearch.get('rssURL'),
    searchType: catalogSearch.get('searchType'),
    shelfName: catalogSearch.get('shelfName'),
    showExternalSearchLink: library.get('showExternalSearchLink'),
    singleBranch: library.get('singleBranch'),
    sortBys: catalogSearch.get('sortBys'),
    suppress: catalogSearch.get('suppress'),
    titleKey: catalogSearch.get('title_key'),
    zeroResults: catalogSearch.get('zeroResults'),
    didNotFindOptions: library.get('didNotFindOptions'),
    chatWidgets: library.get('chatWidgets'),
    library
  };
};

const mapDispatchToProps = dispatch => ({
  searchActions: bindActionCreators(SearchActions, dispatch),
  cardsActions: bindActionCreators(CardsActions, dispatch),
  drawerActions: bindActionCreators(DrawerActions, dispatch),
  recommendationActions: bindActionCreators(RecommendationActions, dispatch),
  availabilityDetailActions: bindActionCreators(AvailabilityDetailActions, dispatch),
  listPresentationActions: bindActionCreators(ListPresentationActions, dispatch),
  groupSearchFeedbackActions: bindActionCreators(GroupSearchFeedbackActions, dispatch)
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(injectIntl(SearchContainer)));
